import {PRISMAX_BACKEND_URL, PRISMAX_DATA_PIPELINE_URL} from '../../../configs/config';
import {
    ADMIN_SESSION_EXPIRED_EVENT,
    ADMIN_SESSION_EXPIRED_MESSAGE,
    ADMIN_SESSION_REFRESHED_EVENT,
    getApiErrorMessage,
} from '../../../utils/authErrors';

export class OrderAdminApiError extends Error {
    constructor(status, code, message) {
        super(message);
        this.name = 'OrderAdminApiError';
        this.status = status;
        this.code = code;
    }
}

let adminRefreshRequest = null;

function expireAdminSession() {
    localStorage.removeItem('prismax_admin_token');
    localStorage.removeItem('prismax_admin_refresh_token');
    window.dispatchEvent(new Event(ADMIN_SESSION_EXPIRED_EVENT));
}

async function refreshAdminSession() {
    if (adminRefreshRequest) return adminRefreshRequest;
    const refreshToken = localStorage.getItem('prismax_admin_refresh_token');
    if (!refreshToken) return null;

    adminRefreshRequest = (async () => {
        const response = await fetch(`${PRISMAX_BACKEND_URL}/api/admin-refresh`, {
            method: 'POST',
            headers: {Authorization: `Bearer ${refreshToken}`},
        });
        const body = await response.json().catch(() => ({}));
        const accessToken = String(body?.access_token || '').trim();
        if (!response.ok || !accessToken) return null;
        localStorage.setItem('prismax_admin_token', accessToken);
        window.dispatchEvent(new CustomEvent(ADMIN_SESSION_REFRESHED_EVENT, {
            detail: {accessToken},
        }));
        return accessToken;
    })().catch(() => null).finally(() => {
        adminRefreshRequest = null;
    });
    return adminRefreshRequest;
}

async function requestEnvelope(baseUrl, path, token, options = {}, allowRefresh = true) {
    const isForm = options.body instanceof FormData;
    const response = await fetch(`${baseUrl}${path}`, {
        ...options,
        headers: {
            ...(isForm ? {} : {'Content-Type': 'application/json'}),
            Authorization: `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    const body = await response.json().catch(() => ({}));
    if (response.status === 401 && allowRefresh) {
        const storedAccessToken = localStorage.getItem('prismax_admin_token');
        const nextAccessToken = storedAccessToken && storedAccessToken !== token
            ? storedAccessToken
            : await refreshAdminSession();
        if (nextAccessToken) {
            return requestEnvelope(baseUrl, path, nextAccessToken, options, false);
        }
        expireAdminSession();
        throw new OrderAdminApiError(
            401,
            body?.code || 'session_expired',
            ADMIN_SESSION_EXPIRED_MESSAGE,
        );
    }
    if (!response.ok || body?.success === false) {
        if (response.status === 401) expireAdminSession();
        throw new OrderAdminApiError(
            response.status,
            body?.code || 'request_failed',
            getApiErrorMessage(body, response.statusText || 'Order admin request failed.'),
        );
    }
    return body;
}

async function request(path, token, options = {}) {
    const body = await requestEnvelope(PRISMAX_BACKEND_URL, path, token, options);
    return body.data;
}

async function pipelineRequest(path, token, options = {}) {
    const body = await requestEnvelope(PRISMAX_DATA_PIPELINE_URL, path, token, options);
    return body.data;
}

function numericOrderId(orderId) {
    const match = String(orderId || '').trim().match(/^(?:ORD-)?(\d+)$/i);
    if (!match) throw new OrderAdminApiError(400, 'invalid_order_id', 'A valid Order ID is required.');
    return Number(match[1]);
}

async function listApprovedOperators(token) {
    const loadPage = (page) => requestEnvelope(
        PRISMAX_BACKEND_URL,
        `/api/admin/get-operator-applications?status=approved&page=${page}`,
        token,
    );
    const first = await loadPage(1);
    const totalPages = Math.max(1, Number(first?.pagination?.total_pages) || 1);
    const remaining = totalPages > 1
        ? await Promise.all(Array.from({length: totalPages - 1}, (_, index) => loadPage(index + 2)))
        : [];
    const byId = new Map();
    [first, ...remaining].forEach((page) => {
        (page?.data || []).forEach((operator) => {
            const userId = Number(operator?.user_id);
            if (!Number.isInteger(userId) || userId <= 0) return;
            byId.set(userId, {
                user_id: userId,
                email: String(operator?.account_email || '').trim(),
                solana_receive_address: String(operator?.solana_receive_address || '').trim(),
            });
        });
    });
    return Array.from(byId.values()).sort((left, right) => (
        left.email.localeCompare(right.email) || left.user_id - right.user_id
    ));
}

async function listTaskScenarios(token) {
    const loadPage = (page) => requestEnvelope(
        PRISMAX_BACKEND_URL,
        `/api/vla-admin/tasks?page=${page}&page_size=100`,
        token,
    );
    const first = await loadPage(1);
    const totalPages = Math.max(1, Number(first?.total_pages) || 1);
    const remaining = totalPages > 1
        ? await Promise.all(Array.from({length: totalPages - 1}, (_, index) => loadPage(index + 2)))
        : [];
    const tasksById = new Map();
    [first, ...remaining].forEach((page) => {
        (page?.data || []).forEach((task) => {
            const taskId = Number(task?.task_id);
            const scenario = String(task?.scenario || '').trim();
            if (!Number.isInteger(taskId) || taskId <= 0 || !scenario) return;
            tasksById.set(taskId, {
                task_id: taskId,
                scenario,
                environment: String(task?.environment || '').trim(),
            });
        });
    });
    return Array.from(tasksById.values()).sort((left, right) => (
        left.scenario.localeCompare(right.scenario) || left.task_id - right.task_id
    ));
}

async function listRobotTypes(token) {
    const robots = await request('/api/admin/order-robot-types', token);
    const robotsById = new Map();
    (robots || []).forEach((robot) => {
        const machineId = Number(robot?.machine_id);
        const productName = String(robot?.product_name || '').trim();
        if (!Number.isInteger(machineId) || machineId <= 0 || !productName) return;
        robotsById.set(machineId, {
            machine_id: machineId,
            manufacture: String(robot?.manufacture || '').trim(),
            product_name: productName,
        });
    });
    return Array.from(robotsById.values()).sort((left, right) => (
        left.product_name.localeCompare(right.product_name) || left.machine_id - right.machine_id
    ));
}

async function listAllDownloadableTaskEpisodes(token, taskId, {jobId = null} = {}) {
    const normalizedTaskId = Number(taskId);
    if (!Number.isInteger(normalizedTaskId) || normalizedTaskId <= 0) {
        throw new OrderAdminApiError(400, 'invalid_task_id', 'A valid Task ID is required.');
    }

    const pageSize = 100;
    let offset = 0;
    const episodesById = new Map();

    while (true) {
        const params = new URLSearchParams({
            admin: 'true',
            task_ids: String(normalizedTaskId),
            limit: String(pageSize),
            offset: String(offset),
        });
        if (Number.isSafeInteger(Number(jobId)) && Number(jobId) > 0) {
            params.set('job_id', String(Number(jobId)));
        }
        const body = await requestEnvelope(
            PRISMAX_DATA_PIPELINE_URL,
            `/data/admin/downloadable-upload-groups?${params.toString()}`,
            token,
        );
        const groups = Array.isArray(body?.data) ? body.data : [];
        groups.forEach((group) => {
            (Array.isArray(group?.episodes) ? group.episodes : []).forEach((episode) => {
                const episodeId = Number(episode?.episode_id);
                if (!Number.isInteger(episodeId) || episodeId <= 0) return;
                episodesById.set(episodeId, {
                    ...episode,
                    episode_id: episodeId,
                    upload_id: episode.upload_id || group.upload_id,
                    task_id: normalizedTaskId,
                    task_name: group.scenario || `Task ${normalizedTaskId}`,
                    scenario: group.scenario || `Task ${normalizedTaskId}`,
                    environment: group.environment || 'Uncategorized',
                    product_name: episode.product_name || group.product_name,
                });
            });
        });

        const pagination = body?.pagination || {};
        if (!pagination.has_more) break;
        const nextOffset = Number(pagination.next_offset);
        if (!Number.isInteger(nextOffset) || nextOffset <= offset) {
            throw new OrderAdminApiError(502, 'invalid_pagination', 'Could not load every Episode for this Task.');
        }
        offset = nextOffset;
    }

    return Array.from(episodesById.values());
}

const ADD_ORDER_EPISODE_BATCH_SIZE = 500;

async function addOrderEpisodes(token, orderId, episodeIds) {
    const results = {
        added_episode_ids: [],
        already_in_order_episode_ids: [],
        order: null,
    };
    for (let offset = 0; offset < episodeIds.length; offset += ADD_ORDER_EPISODE_BATCH_SIZE) {
        const batch = episodeIds.slice(offset, offset + ADD_ORDER_EPISODE_BATCH_SIZE);
        const result = await request(
            `/api/admin/orders/${encodeURIComponent(orderId)}/additional-episodes`,
            token,
            {
                method: 'POST',
                body: JSON.stringify({episode_ids: batch}),
            },
        );
        results.added_episode_ids.push(...(result?.added_episode_ids || []));
        results.already_in_order_episode_ids.push(...(result?.already_in_order_episode_ids || []));
        if (result?.order) results.order = result.order;
    }
    return results;
}

export const orderAdminApi = {
    listTaskScenarios,
    listRobotTypes,
    listAllDownloadableTaskEpisodes,
    listApprovedOperators,
    listOrganizations: (token) => request('/api/admin/organizations', token),
    getOrganization: (token, organizationId) => request(`/api/admin/organizations/${organizationId}`, token),
    createOrganization: (token, values) => request('/api/admin/organizations', token, {
        method: 'POST',
        body: JSON.stringify(values),
    }),
    addOwner: (token, organizationId, ownerEmail) => request(`/api/admin/organizations/${organizationId}/owners`, token, {
        method: 'POST',
        body: JSON.stringify({owner_email: ownerEmail}),
    }),
    removeOrganizationMember: (token, organizationId, userId) => request(`/api/admin/organizations/${organizationId}/members/${userId}`, token, {
        method: 'DELETE',
        body: JSON.stringify({confirmed: true}),
    }),
    addInternalOwner: (token, organizationId, ownerEmail) => request(`/api/admin/organizations/${organizationId}/internal-owners`, token, {
        method: 'POST',
        body: JSON.stringify({owner_email: ownerEmail}),
    }),
    removeInternalOwner: (token, organizationId, userId) => request(`/api/admin/organizations/${organizationId}/internal-owners/${userId}`, token, {
        method: 'DELETE',
    }),
    listOrders: (token, filters = {}) => {
        const params = new URLSearchParams();
        if (filters.organizationId) params.set('organization_id', String(filters.organizationId));
        if (filters.status) params.set('status', filters.status);
        const suffix = params.size ? `?${params}` : '';
        return request(`/api/admin/orders${suffix}`, token);
    },
    getOrder: (token, orderId) => request(`/api/admin/orders/${encodeURIComponent(orderId)}`, token),
    linkOrderItemTask: (token, orderId, itemId, taskId) => request(
        `/api/admin/orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(itemId)}/link-task`,
        token,
        {
            method: 'POST',
            body: JSON.stringify({task_id: Number(taskId)}),
        },
    ),
    getCatalogTaskFormSchema: async (token) => {
        const body = await requestEnvelope(
            PRISMAX_BACKEND_URL,
            '/api/vla-admin/tasks?page=1&page_size=1',
            token,
        );
        return body.meta || {};
    },
    createCatalogTask: (token, row) => request('/api/vla-admin/tasks', token, {
        method: 'POST',
        body: JSON.stringify({row}),
    }),
    listOrderEpisodes: (token, orderId) => request(
        `/api/admin/orders/${encodeURIComponent(orderId)}/episodes`,
        token,
    ),
    addOrderEpisodes,
    removeOrderEpisode: (token, orderId, episodeId) => request(
        `/api/admin/orders/${encodeURIComponent(orderId)}/additional-episodes/${encodeURIComponent(episodeId)}`,
        token,
        {
            method: 'DELETE',
            body: JSON.stringify({confirmed: true}),
        },
    ),
    listOrderJobs: (token, orderId) => pipelineRequest(
        `/data/admin/jobs?order_id=${numericOrderId(orderId)}`,
        token,
    ),
    getJob: (token, jobId) => pipelineRequest(
        `/data/admin/jobs/${encodeURIComponent(jobId)}`,
        token,
    ),
    createLinkedJob: (token, orderId, values) => pipelineRequest('/data/admin/jobs', token, {
        method: 'POST',
        body: JSON.stringify({...values, order_id: numericOrderId(orderId)}),
    }),
    setJobOrder: (token, jobId, orderId, confirmed = false) => pipelineRequest(
        `/data/admin/jobs/${encodeURIComponent(jobId)}/order`,
        token,
        {
            method: 'PATCH',
            body: JSON.stringify({
                order_id: orderId == null ? null : numericOrderId(orderId),
                confirmed: Boolean(confirmed),
            }),
        },
    ),
    createOrder: (token, organizationId, values) => request(`/api/admin/organizations/${organizationId}/orders`, token, {
        method: 'POST',
        body: JSON.stringify(values),
    }),
    updateOrder: (token, orderId, values) => request(`/api/admin/orders/${encodeURIComponent(orderId)}`, token, {
        method: 'PATCH',
        body: JSON.stringify(values),
    }),
    approveOrder: (token, orderId, hourlyRate) => request(`/api/admin/orders/${encodeURIComponent(orderId)}/approve`, token, {
        method: 'POST',
        body: JSON.stringify({hourly_rate: Number(hourlyRate)}),
    }),
    markDelivering: (token, orderId) => request(`/api/admin/orders/${encodeURIComponent(orderId)}/mark-delivering`, token, {
        method: 'POST',
        body: '{}',
    }),
    cancelOrder: (token, orderId) => request(`/api/admin/orders/${encodeURIComponent(orderId)}/cancel`, token, {
        method: 'POST',
        body: JSON.stringify({confirmed: true}),
    }),
    listInvoices: (token, organizationId = '') => request(
        `/api/admin/invoices${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ''}`,
        token,
    ),
    issueInvoice: (token, orderIds, file) => {
        const form = new FormData();
        orderIds.forEach((orderId) => form.append('order_ids', orderId));
        form.append('file', file);
        return request('/api/admin/invoices', token, {method: 'POST', body: form});
    },
    markInvoicePaid: (token, invoiceId) => request(`/api/admin/invoices/${encodeURIComponent(invoiceId)}/mark-paid`, token, {
        method: 'POST',
        body: JSON.stringify({confirmed: true}),
    }),
};
